import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import { TextHighlight } from 'designSystem/components/TextHighlight'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import { QuotedSelectionScroll } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/docChatStyles'
import {
  canSubmitDocChatFeedback,
  DOCCHAT_FEEDBACK_COMMENT_MAX_LENGTH,
  DOCCHAT_FEEDBACK_REASON_LABELS,
  DOCCHAT_FEEDBACK_REASONS,
  type DocChatFeedbackReason,
  toDocChatFeedbackReasons,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFeedback'
import React, { useCallback, useId, useState } from 'react'

export type DocChatFeedbackDialogProps = {
  open: boolean
  isSubmitting: boolean
  submitted: boolean
  quote?: string | null
  error: string | null
  onOpenChange: (open: boolean) => void
  onSubmit: (reasons: DocChatFeedbackReason[], comment: string) => void
}

export function DocChatFeedbackDialog({
  open,
  isSubmitting,
  submitted,
  quote = null,
  error,
  onOpenChange,
  onSubmit,
}: DocChatFeedbackDialogProps): React.ReactElement {
  const { t, i18n } = useLingui()
  const commentId = useId()
  const [reasons, setReasons] = useState<DocChatFeedbackReason[]>([])
  const [comment, setComment] = useState('')
  const canSubmit = canSubmitDocChatFeedback(reasons, comment) && !isSubmitting

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isSubmitting) onOpenChange(nextOpen)
    },
    [isSubmitting, onOpenChange]
  )

  const handleReasonsChange = useCallback((values: string[]) => {
    setReasons(toDocChatFeedbackReasons(values))
  }, [])

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(event.currentTarget.value)
    },
    []
  )

  const handleSubmit = useCallback(() => {
    if (canSubmit) onSubmit(reasons, comment)
  }, [canSubmit, comment, onSubmit, reasons])

  const handleCancel = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size='md'>
          <DialogHeader>
            <DialogTitle>{t`Thanks for your feedback`}</DialogTitle>
            <DialogDescription>
              {t`Your feedback was sent to the DocChat team.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='primaryRaised'
              onClick={handleCancel}>
              {t`Close`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{t`Share feedback`}</DialogTitle>
          <DialogDescription>
            {t`What was wrong with this answer? Your feedback is used to improve DocChat.`}
          </DialogDescription>
        </DialogHeader>
        {quote ? (
          <Box
            display='flex'
            direction='column'
            gap='xs'
            padding='md'
            radius='md'
            border='default'
            surface='muted'>
            <Text variant='caption' color='muted'>
              {t`Selected text`}
            </Text>
            <QuotedSelectionScroll>
              <Text as='p' size='sm'>
                <TextHighlight color='blue'>{quote}</TextHighlight>
              </Text>
            </QuotedSelectionScroll>
          </Box>
        ) : null}
        <ToggleGroup
          type='multiple'
          variant='outline'
          size='sm'
          spacing={2}
          width='full'
          wrap='wrap'
          value={reasons}
          disabled={isSubmitting}
          aria-label={t`Feedback reasons`}
          onValueChange={handleReasonsChange}>
          {DOCCHAT_FEEDBACK_REASONS.map((reason) => (
            <ToggleGroupItem key={reason} value={reason}>
              {i18n._(DOCCHAT_FEEDBACK_REASON_LABELS[reason])}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Textarea
          sizing='bounded'
          id={commentId}
          aria-label={t`Feedback details`}
          placeholder={t`Share details (optional)`}
          value={comment}
          maxLength={DOCCHAT_FEEDBACK_COMMENT_MAX_LENGTH}
          disabled={isSubmitting}
          onChange={handleCommentChange}
        />
        {error ? (
          <Text as='p' size='sm' color='destructive' role='alert'>
            {error}
          </Text>
        ) : null}
        <DialogFooter>
          <Button
            type='button'
            variant='inputRaised'
            disabled={isSubmitting}
            onClick={handleCancel}>
            {t`Cancel`}
          </Button>
          <Button
            type='button'
            variant='primaryRaised'
            disabled={!canSubmit}
            onClick={handleSubmit}>
            {isSubmitting ? t`Submitting…` : t`Submit`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
